import { useProductList } from "@/api/services/admin/product";
import { TProductListItem } from "@/api/services/admin/product/dtos/get-product-list.dto";
import { Checkbox } from "@/components/elements/Checkbox";
import { EllipsisText } from "@/components/elements/EllipsisText";
import { SectionCard } from "@/components/elements/SectionCard";
import { Table, TableSorting } from "@/components/elements/Table";
import { EMPTY_STR } from "@/constants/string";
import { ContractServiceBadge } from "@/features/organization/components/ContractServiceBadge";
import { useSorting } from "@/hooks/useSorting";
import { useTable } from "@/hooks/useTable";
import { black, text } from "@/theme/colors";
import { TApiSortsReq } from "@/types/api";
import { EProductCode } from "@/types/enum";
import { Stack, Typography } from "@mui/material";
import { MRT_ColumnDef } from "material-react-table";
import { FC, useEffect, useMemo, useRef } from "react";

/** プロダクト作成時に選択可能なプロダクトコード（連携済みのスポークのみ）。 */
const SELECTABLE_PRODUCT_CODES: string[] = [EProductCode.VISA, EProductCode.ACCOUNT];

type Props = {
  selectedProductIds: number[];
  onToggleProduct: (productId: number) => void;
  error?: string;
};

export const OrganizationCreateProductSelect: FC<Props> = ({ selectedProductIds, onToggleProduct, error }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const { sorts } = useSorting();
  const { data: productData, isLoading } = useProductList({
    sorts: sorts.length > 0 ? (sorts as TApiSortsReq[]) : undefined,
  });
  const products = useMemo(() => (productData?.data ?? []).filter((product) => SELECTABLE_PRODUCT_CODES.includes(product.code)), [productData]);

  const columns: MRT_ColumnDef<TProductListItem>[] = useMemo(
    () => [
      {
        id: "select",
        header: "",
        size: 50,
        Cell: ({ row }) => (
          <Checkbox checked={selectedProductIds.includes(row.original.id)} onChange={() => onToggleProduct(row.original.id)} sx={{ p: 0 }} />
        ),
      },
      {
        id: "code",
        header: "",
        size: 60,
        Cell: ({ row }) => (
          <ContractServiceBadge products={[{ code: row.original.code, singleCharLabel: row.original.singleCharLabel, name: row.original.name }]} />
        ),
      },
      {
        accessorKey: "name",
        header: "プロダクト名",
        Header: () => (
          <TableSorting name="name">
            <Typography variant="body14Bold" color={text.primary}>
              プロダクト名
            </Typography>
          </TableSorting>
        ),
        size: 176,
        Cell: ({ row }) => <EllipsisText title={row.original.name ?? EMPTY_STR.TEXT} variant="body14" maxWidth={180} />,
      },
      {
        id: "plan",
        header: "システム情報",
        size: 176,
        Cell: () => EMPTY_STR.TEXT,
      },
      {
        id: "activeUserCount",
        header: "有効ユーザー数",
        size: 130,
        Cell: () => EMPTY_STR.TEXT,
      },
      {
        id: "lastLoginAt",
        header: "最終ログイン",
        size: 148,
        Cell: () => EMPTY_STR.TEXT,
      },
      {
        id: "createdAt",
        header: "登録日",
        size: 108,
        Cell: () => EMPTY_STR.TEXT,
      },
    ],
    [selectedProductIds, onToggleProduct],
  );

  const { table } = useTable({
    columns,
    rows: products,
  });

  useEffect(() => {
    if (error) {
      containerRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [error]);

  return (
    <div ref={containerRef}>
      <SectionCard title="契約対象" titleVariant="head20Bold" maxWidth="100%" titleColor={black[30]}>
        <Stack gap={2}>
          <Table table={table} isLoading={isLoading} />
          {error && (
            <Typography variant="body14" color={text.error}>
              {error}
            </Typography>
          )}
          <Typography variant="body14" color={text.secondary}>
            お客様が契約するプロダクトをチェックしてください。新規契約時は仮登録されるため、保存後に選択して登録を進めてください。
          </Typography>
        </Stack>
      </SectionCard>
    </div>
  );
};
